import { getApiFailureMessage } from "../../lib/gboApiClient";
import { useId, useLayoutEffect, useRef } from "preact/hooks";
import { Link } from "wouter-preact";
import { CheckIcon, ChevronDownIcon } from "../../components/icons";
import { useLogoutPortalMutation, type PortalAccount } from "../../store/portalAuthApi";
import { formatMoney } from "../../lib/format";
import { routes } from "../../lib/route";
import "./portalAccounts.css";

type Props = {
  accounts: PortalAccount[];
  currentCustomerId: string | null;
  pending?: boolean;
  onSelect: (customerId: string) => Promise<void>;
};

export function AccountSwitcher({ accounts, currentCustomerId, pending = false, onSelect }: Props) {
  const menuId = useId();
  const [logout, logoutResult] = useLogoutPortalMutation();
  const focusTarget = useRef<HTMLElement | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const restoreFocusAfterSelection = useRef(false);
  const current = accounts.find((account) => account.customerId === currentCustomerId);

  async function select(customerId: string) {
    restoreFocusAfterSelection.current = true;
    await onSelect(customerId);
    menu.current?.hidePopover();
  }

  useLayoutEffect(() => {
    if (restoreFocusAfterSelection.current && !pending && accounts.length) {
      focusTarget.current?.focus();
      restoreFocusAfterSelection.current = false;
    }
  }, [pending, accounts, currentCustomerId]);

  return (
    <div
      class="account-switcher"
      style={{ "--account-menu-anchor": `--${menuId}` }}
      onFocusOut={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node))
          menu.current?.hidePopover();
      }}
    >
      <button
        class="account-switcher-trigger"
        ref={(element) => {
          focusTarget.current = element;
        }}
        type="button"
        disabled={pending || logoutResult.isLoading}
        aria-label={current ? `Account, ${current.name} ${current.code}` : "Account"}
        aria-haspopup="dialog"
        popovertarget={menuId}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            menu.current?.showPopover({ source: event.currentTarget });
          }
        }}
      >
        <span class="account-switcher-label">
          <strong>{current?.name ?? "Account"}</strong>
          {current && <span>{current.code}</span>}
        </span>
        <span class="account-switcher-chevron" aria-hidden="true">
          <ChevronDownIcon size={16} />
        </span>
      </button>
      <div
        class="account-switcher-menu"
        id={menuId}
        ref={menu}
        popover="auto"
        role="dialog"
        aria-label="Accounts"
        onBeforeToggle={(event) => {
          if (event.newState !== "open" || !focusTarget.current) return;
          const bounds = focusTarget.current.getBoundingClientRect();
          event.currentTarget.style.setProperty("--account-menu-top", `${bounds.bottom}px`);
          event.currentTarget.style.setProperty(
            "--account-menu-right",
            `${window.innerWidth - bounds.right}px`,
          );
        }}
        onToggle={(event) => {
          if (event.newState !== "open") return;
          const selected = event.currentTarget.querySelector<HTMLButtonElement>(
            'button[aria-pressed="true"]',
          );
          (selected ?? event.currentTarget.querySelector<HTMLElement>("button, a"))?.focus();
        }}
        onKeyDown={(event) => {
          const controls = Array.from(
            menu.current?.querySelectorAll<HTMLElement>("button:not(:disabled), a") ?? [],
          );
          const index = controls.indexOf(document.activeElement as HTMLElement);
          let next: number | undefined;
          if (event.key === "ArrowDown") next = (index + 1) % controls.length;
          if (event.key === "ArrowUp") next = (index - 1 + controls.length) % controls.length;
          if (event.key === "Home") next = 0;
          if (event.key === "End") next = controls.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            controls[next]?.focus();
          }
        }}
      >
        {accounts.length > 0 && (
          <AccountOptions
            accounts={accounts}
            currentCustomerId={currentCustomerId}
            pending={pending || logoutResult.isLoading}
            onSelect={select}
          />
        )}
        <footer class="account-switcher-footer">
          <Link
            href={`~${routes.portal.linkedAccounts()}`}
            onClick={() => menu.current?.hidePopover()}
          >
            Manage linked accounts
          </Link>
          <button
            class="link-action"
            type="button"
            disabled={pending || logoutResult.isLoading}
            onClick={() => void logout()}
          >
            {logoutResult.isLoading ? "Signing out…" : "Sign out"}
          </button>
          {logoutResult.error && (
            <p class="payment-error" role="alert">
              {getApiFailureMessage(logoutResult.error, "Unable to sign out. Please try again.")}
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}

export function AccountOptions({ accounts, currentCustomerId, pending = false, onSelect }: Props) {
  return (
    <div class="account-options" aria-label="Available accounts">
      {accounts.map((account) => {
        const selected = account.customerId === currentCustomerId;
        const words = account.name.trim().split(/\s+/);
        const initials = words
          .slice(0, 2)
          .map((word) => word[0] ?? "")
          .join("")
          .toLocaleUpperCase();
        return (
          <button
            class={`account-option${selected ? " account-option--selected" : ""}`}
            key={account.customerId}
            type="button"
            aria-pressed={selected}
            disabled={pending}
            onClick={() => !selected && void onSelect(account.customerId)}
          >
            <span class="account-option-avatar" aria-hidden="true">
              {selected ? <CheckIcon size={20} /> : initials}
            </span>
            <span class="account-option-details">
              <strong>{account.name}</strong>
              <span>
                {account.code} · {account.serviceAddress}
              </span>
            </span>
            {account.balance === 0 ? (
              <span class="account-paid">Paid</span>
            ) : (
              <span class="account-option-balance">
                {account.balance < 0
                  ? `${formatMoney(-account.balance)} credit`
                  : formatMoney(account.balance)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
